<script setup>
/**
 * CrmWinComprasForm — form de compras ao marcar Ganho (T5 / incremento e).
 *
 * Renderizado inline no painel acima do rodapé quando o usuário clica "Marcar Ganho".
 * Permite adicionar N linhas de compra (item obrigatório) antes de confirmar.
 * Confirmar sem linhas também é válido — win sem compra registra o ganho limpo.
 * Emite 'confirm' com array de compras, ou 'cancel' para voltar.
 */
import { ref } from 'vue';

const props = defineProps({
  submitting: { type: Boolean, default: false },
  // Mensagem de erro inline vinda do pai (outcomeError do CrmDealPanel).
  error: { type: String, default: '' },
});

const emit = defineEmits(['confirm', 'cancel']);

// Cada linha: { item, variacao, quantidade, data_compra }
function newLinha() {
  // FE-L2: usa data local, não UTC — evita virar dia anterior perto da meia-noite no BRT.
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return {
    item: '',
    variacao: '',
    quantidade: 1,
    data_compra: `${yyyy}-${mm}-${dd}`,
  };
}

const linhas = ref([newLinha()]);

function addLinha() {
  linhas.value.push(newLinha());
}

function removeLinha(idx) {
  linhas.value.splice(idx, 1);
}

function confirm() {
  // FE-H1: guard defensivo consistente com o popover de Perda.
  if (props.submitting) return;

  // FE-M1: quantidade sanitizada — inteiro >= 1, sem 0/negativo/decimal.
  const compras = linhas.value
    .filter(l => l.item.trim())
    .map(l => ({
      item: l.item.trim(),
      variacao: l.variacao.trim() || null,
      quantidade: Math.max(1, Math.floor(Number(l.quantidade) || 1)),
      data_compra: l.data_compra || null,
    }));
  emit('confirm', compras);
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -- CRM é pt-BR locked (v1) -->
  <!--
    Form inline — aparece acima do rodapé ao clicar Marcar Ganho.
    Ocupa a largura total do painel com padding lateral padrão.
  -->
  <div class="px-4 pb-3 pt-3 border-t border-n-weak flex flex-col gap-3">
    <!-- Label. custom (redesign Modeloja): eyebrow em ink, não teal (brand-only). -->
    <p class="text-mj-13 font-500 text-n-slate-11">
      Registrar compras (opcional)
    </p>

    <!-- Linhas de compra -->
    <div class="flex flex-col gap-2">
      <div
        v-for="(linha, idx) in linhas"
        :key="idx"
        class="flex flex-col gap-1.5 p-2.5 rounded-lg border border-n-weak bg-n-surface-1"
      >
        <!-- Linha topo: item + remover -->
        <div class="flex items-center gap-2">
          <input
            v-model="linha.item"
            type="text"
            placeholder="Item *"
            maxlength="120"
            class="flex-1 h-7 px-2 text-mj-13 text-n-slate-12 font-400 rounded-md border border-n-weak bg-n-background placeholder:text-n-slate-10 focus:outline-none focus:ring-1 focus:ring-n-slate-8 focus:border-transparent transition-colors duration-mj-toque"
          />
          <button
            v-if="linhas.length > 1"
            class="size-6 flex items-center justify-center rounded-md text-n-slate-9 hover:text-n-ruby-11 hover:bg-n-ruby-2 transition-colors duration-mj-toque flex-shrink-0"
            :title="`Remover linha ${idx + 1}`"
            @click="removeLinha(idx)"
          >
            <span class="i-lucide-trash-2 size-3.5" />
          </button>
        </div>

        <!-- Linha baixo: variação + qtd + data -->
        <div class="flex items-center gap-1.5">
          <input
            v-model="linha.variacao"
            type="text"
            placeholder="Variação"
            maxlength="60"
            class="flex-1 h-7 px-2 text-mj-13 text-n-slate-12 font-400 rounded-md border border-n-weak bg-n-background placeholder:text-n-slate-10 focus:outline-none focus:ring-1 focus:ring-n-slate-8 focus:border-transparent transition-colors duration-mj-toque min-w-0"
          />
          <input
            v-model.number="linha.quantidade"
            type="number"
            min="1"
            max="999"
            step="1"
            placeholder="Qtd"
            class="w-14 h-7 px-2 text-mj-13 text-n-slate-12 font-400 rounded-md border border-n-weak bg-n-background placeholder:text-n-slate-10 tabular-nums focus:outline-none focus:ring-1 focus:ring-n-slate-8 focus:border-transparent transition-colors duration-mj-toque flex-shrink-0"
          />
          <input
            v-model="linha.data_compra"
            type="date"
            class="h-7 px-2 text-mj-12 text-n-slate-12 font-400 rounded-md border border-n-weak bg-n-background tabular-nums focus:outline-none focus:ring-1 focus:ring-n-slate-8 focus:border-transparent transition-colors duration-mj-toque flex-shrink-0"
          />
        </div>
      </div>
    </div>

    <!-- Adicionar linha -->
    <button
      class="w-full h-7 inline-flex items-center justify-center gap-1.5 text-mj-12 font-500 text-n-slate-11 border border-dashed border-n-weak rounded-lg hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors duration-mj-toque"
      @click="addLinha"
    >
      <span class="i-lucide-plus size-3.5" />
      Adicionar item
    </button>

    <!-- Erro inline (FE-H2: vem do pai via prop) -->
    <p v-if="error" class="text-mj-11 text-n-ruby-11">{{ error }}</p>

    <!-- Actions -->
    <div class="flex items-center gap-2">
      <button
        class="flex-1 h-8 text-mj-12 font-500 text-n-slate-11 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors duration-mj-toque"
        @click="emit('cancel')"
      >
        Cancelar
      </button>
      <!-- custom (2026-06-24 · pastel): "Confirmar Ganho" = ação positiva terminal em
           VERDE PASTEL (#DCFCE7 / texto #15803D). Espelha "Marcar Ganho" no CrmDealPanel. -->
      <button
        :disabled="submitting"
        class="flex-1 h-8 text-mj-12 font-600 text-mj-ganha-ink rounded-lg bg-mj-ganha-bg hover:bg-[#BBF7D0] dark:bg-n-teal-3 dark:text-n-teal-11 dark:hover:bg-n-teal-4 transition-colors duration-mj-toque disabled:opacity-50 disabled:cursor-not-allowed"
        @click="confirm"
      >
        {{ submitting ? 'Salvando…' : 'Confirmar Ganho' }}
      </button>
    </div>
  </div>
</template>
